import {
  useMoonshotEmailVerificationAddEmailMutation,
  useMoonshotEmailVerificationResendEmailMutation,
} from '@trello/graphql/generated';
import { ApolloError } from 'apollo-client';
import {
  ButtonSizes,
  PrimaryButton,
} from 'app/common/components/controls/button/button';
import preventDefault from 'app/gamma/src/util/prevent-default';
import { EmailSniperLink } from 'app/src/components/EmailSniperLink';
import {
  EmailError,
  emailErrorMapping,
  EmailErrors,
} from 'app/src/components/EmailError';
import { Spinner } from 'app/src/components/Spinner';
import classNames from 'classnames';
import React, { useState } from 'react';
import styles from './MoonshotEmailVerification.less';
import { MoonshotError } from './MoonshotError';

export enum MoonshotEmailVerificationPage {
  Verification = 'verification',
  ResendVerification = 'resendVerification',
  ChangeEmail = 'changeEmail',
}

interface ChangeEmailComponentProps {
  updatePage: (page: MoonshotEmailVerificationPage) => void;
  onSubmitEmail?: (email: string) => void;
  email: string;
}

const ChangeEmailComponent: React.FC<ChangeEmailComponentProps> = ({
  updatePage,
  onSubmitEmail = () => {},
  email: currentEmail = '',
}) => {
  const [email, updateEmail] = useState(currentEmail);
  const [error, updateError] = useState(EmailErrors.None);
  const [isLoading, updateLoading] = useState(false);
  const [addNewEmail] = useMoonshotEmailVerificationAddEmailMutation();

  const onValidateEmail = async (newEmail: string) => {
    updateLoading(true);

    try {
      await addNewEmail({
        variables: {
          email: newEmail,
        },
      });

      onSubmitEmail(newEmail);
    } catch (err) {
      // GQL Networks errors start with this string so we need to
      // remove it so we can match on the original message
      const errMessage = (err as ApolloError).message.replace(
        'Network error: ',
        '',
      );

      updateError(emailErrorMapping(errMessage));
    } finally {
      updateLoading(false);
    }
  };

  return (
    <form onSubmit={preventDefault(() => onValidateEmail(email))}>
      <h2>Need to change your email?</h2>
      <label htmlFor="email">
        Work Email{' '}
        {isLoading && <Spinner small wrapperClassName={styles.spinner} />}
      </label>
      <input
        id="change-email"
        type="email"
        name="email"
        value={email}
        onChange={(e) => updateEmail(e.target.value)}
        required
      />
      {error !== EmailErrors.None && (
        <EmailError error={error} email={email}>
          {(errorMessage) => <MoonshotError>{errorMessage}</MoonshotError>}
        </EmailError>
      )}
      <p>
        We sent a verification message to the address above. If you need to use
        a different work email address, you can change it here.
      </p>
      <PrimaryButton
        size={ButtonSizes.FULLWIDTH}
        className={styles.button}
        type="submit"
        disabled={isLoading}
      >
        Send Verification Email
      </PrimaryButton>
      <p className={styles.actions}>
        <a
          href="#"
          onClick={preventDefault(() =>
            updatePage(MoonshotEmailVerificationPage.Verification),
          )}
        >
          Cancel. Go back.
        </a>
      </p>
    </form>
  );
};

interface VerificationComponentProps {
  title: string;
  email: string;
  updatePage: (page: MoonshotEmailVerificationPage) => void;
}

const VerificationComponent: React.FC<VerificationComponentProps> = ({
  title,
  email,
  updatePage,
}) => {
  const [
    resendEmailVerification,
  ] = useMoonshotEmailVerificationResendEmailMutation();

  return (
    <>
      <img
        src={require('resources/images/moonshot/email-verification.svg')}
        alt=""
        role="presentation"
      />
      <h2>{title}</h2>
      <p>
        We sent an email to <strong>{email}</strong>. <br />
        Please verify your email in order to upgrade your team.
      </p>
      <EmailSniperLink email={email}>
        {(indexUrl) =>
          indexUrl ? (
            <a className={styles.button} href={indexUrl} target="_blank">
              Check your Email
            </a>
          ) : null
        }
      </EmailSniperLink>
      <p>
        <a
          href="#"
          onClick={preventDefault(async () => {
            await resendEmailVerification({
              variables: { email },
            });

            updatePage(MoonshotEmailVerificationPage.ResendVerification);
          })}
        >
          Resend Verification
        </a>{' '}
        or{' '}
        <a
          href="#"
          onClick={preventDefault(() =>
            updatePage(MoonshotEmailVerificationPage.ChangeEmail),
          )}
        >
          Change Email
        </a>
      </p>
    </>
  );
};

interface MoonshotEmailVerificationProps {
  initialPage?: MoonshotEmailVerificationPage;
  email: string;
}

export const MoonshotEmailVerification: React.FC<
  MoonshotEmailVerificationProps
> = ({
  initialPage = MoonshotEmailVerificationPage.Verification,
  email: currentEmail,
}) => {
  const [page, updatePage] = useState(initialPage);
  const [email, updateEmail] = useState(currentEmail);

  return (
    <div className={styles.emailVerificationOverlay}>
      <div className={classNames(styles.emailVerification, styles[page])}>
        {page === MoonshotEmailVerificationPage.ChangeEmail ? (
          <ChangeEmailComponent
            updatePage={updatePage}
            email={email}
            onSubmitEmail={(newEmail) => {
              updateEmail(newEmail);
              updatePage(MoonshotEmailVerificationPage.Verification);
            }}
          />
        ) : (
          <VerificationComponent
            title={
              page === MoonshotEmailVerificationPage.Verification
                ? "You're almost there!"
                : 'Got it! We sent it again'
            }
            email={email}
            updatePage={updatePage}
          />
        )}
      </div>
    </div>
  );
};
